export const REVIEW_BINDING_HEADER = 'X-Orovia-Review-Binding'
export const REVIEW_BINDING_STORAGE_KEY = 'orovia.review.binding.v1'

const REVIEW_BINDING = /^[A-Za-z0-9_-]{43}$/

export interface ReviewBindingStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

export interface StoredReviewBinding {
  version: 1
  binding: string
  returnTarget: 'main' | 'portal'
}

export function isReviewBinding(value: unknown): value is string {
  return typeof value === 'string' && REVIEW_BINDING.test(value)
}

export function generateReviewBinding(
  random: Pick<Crypto, 'getRandomValues'> = crypto,
): string {
  const bytes = new Uint8Array(32)
  random.getRandomValues(bytes)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  const binding = btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
  if (!isReviewBinding(binding)) throw new Error('Unable to create review binding')
  return binding
}

export function persistReviewBinding(
  storage: ReviewBindingStorage,
  binding: string,
  returnTarget: 'main' | 'portal',
) {
  if (!isReviewBinding(binding)) return false
  try {
    storage.setItem(REVIEW_BINDING_STORAGE_KEY, JSON.stringify({
      version: 1,
      binding,
      returnTarget,
    } satisfies StoredReviewBinding))
    return true
  } catch {
    clearReviewBinding(storage)
    return false
  }
}

export function readReviewBinding(
  storage: ReviewBindingStorage,
): StoredReviewBinding | null {
  try {
    const raw = storage.getItem(REVIEW_BINDING_STORAGE_KEY)
    if (!raw) return null
    const value = JSON.parse(raw) as Record<string, unknown>
    if (
      !value || typeof value !== 'object' || Array.isArray(value) ||
      Object.keys(value).sort().join(',') !== 'binding,returnTarget,version' ||
      value.version !== 1 ||
      !isReviewBinding(value.binding) ||
      (value.returnTarget !== 'main' && value.returnTarget !== 'portal')
    ) {
      clearReviewBinding(storage)
      return null
    }
    return value as unknown as StoredReviewBinding
  } catch {
    clearReviewBinding(storage)
    return null
  }
}

export function clearReviewBinding(storage: ReviewBindingStorage) {
  try {
    storage.removeItem(REVIEW_BINDING_STORAGE_KEY)
  } catch {
    // A blocked storage implementation is equivalent to no resumable binding.
  }
}
